import { useState, useEffect, FC, useMemo } from 'react'
import { Select } from 'entities/Select'
import { useBranchStore } from 'shared/store/branchesSlice'
import { QRCodeCanvas } from 'qrcode.react'
import {
  DisclaimerText,
  MessageCopiiedToaster,
  QRCodeContainer,
  QRCodeLayout,
  QRCodeText,
  SelectContainer,
  SelectContainerBottomAligned
} from './styled'
import { GridDiv } from 'entities/GridDiv'
import { Input } from 'entities/Input'
import { Button } from 'entities/Button'
import { useGlobalStore } from 'shared/store/globalSlice'
import { getGuid } from 'shared/helpers/getGUID'
import html2canvas from 'html2canvas'
import { saveAs } from 'file-saver'
import { useCaptionsStore } from 'shared/store/captionsSlice'
import { RenderIf } from 'shared/helpers/renderIf'
import { useQrCodeStore } from 'shared/store/qrCodeSlice'
import { POST } from '../../../shared/api/POST'
import { useConfigStore } from 'shared/store/configSlice'

export const QRCodeBuilder: FC<QRCodeBuilderType> = ({ isExtended = false }) => {
  const branchList = useBranchStore().branchList
  const selectedBranch = useBranchStore().branchId
  const isCCSelected = useGlobalStore().isCCSelected
  const isUrlExtended = useConfigStore().config?.menuConfig?.QRMENU?.isUrlExtended

  const { roomsList, tablesList, loadCaptions } = useCaptionsStore()
  const { changeQrCodeValue, discardQrCodeValue } = useQrCodeStore()
  const [qrCodeValueInner, setQrCodeValueInner] = useState('')
  const [selectedRoom, setSelectedRoom] = useState<number>()
  const [selectedCaption, setSelectedCaption] = useState<number>()
  const [isToasterOpen, setIsToasterOpen] = useState<boolean>(false)
  const [isLinkGenerating, setIsLinkGenerating] = useState<boolean>(false)

  useEffect(() => {
    if (isExtended) {
      loadCaptions()
    }
  }, [isExtended])

  const restaurantDesignId = useMemo(() => {
    const id = branchList?.find(branch => branch.Intern === selectedBranch)?.restaurantDesignId
    const num = Number(id)
    return (typeof id === 'string' || typeof id === 'number') && !isNaN(num) ? num : undefined
  }, [selectedBranch, branchList])

  useEffect(() => {
    setSelectedRoom(
      roomsList?.filter(room1 => {
        if (restaurantDesignId) {
          return room1.restaurantDesignId === restaurantDesignId
        }
        return true
      })?.[0]?.id
    )
  }, [roomsList?.length, restaurantDesignId])

  useEffect(() => {
    setSelectedCaption(tablesList?.filter(i => i.roomId === selectedRoom)?.[0]?.id)
  }, [tablesList?.length, selectedRoom])

  useEffect(() => {
    discardQrCodeValue()
    if (isCCSelected || !isUrlExtended) {
      generateLink()
    } else {
      setQrCodeValueInner('')
    }
  }, [selectedBranch, selectedCaption, isUrlExtended, isCCSelected])

  const getEncodedOrRaw = async (url = '', fields?: string[]) => {
    try {
      const encoded = await POST.encodeUrlParams(url, fields)
      return encoded?.encodedUrl || url
    } catch (e) {
      console.error('Error in *getEncodedOrRaw*:', e)
      return url
    }
  }

  const generateLink = async () => {
    try {
      setIsLinkGenerating(true)

      const company = await getGuid()
      const QRMenuClientId = branchList?.find(
        item => +item.Intern === selectedBranch
      )?.QRMenuClientId

      let finalUrl = ''

      // Case 1
      if (isCCSelected && company) {
        const url = `${process.env.REACT_APP_CLICKCOLLECT_URL}/?guid=${company}`
        finalUrl = await getEncodedOrRaw(url)
      }
      // Case 2
      else if (!isCCSelected && !isExtended && QRMenuClientId && company) {
        const url = `${process.env.REACT_APP_QRMENU_URL}/?register=${QRMenuClientId}&guid=${company}`
        finalUrl = await getEncodedOrRaw(url, ['register', 'guid'])
      }
      // Case 3
      else if (!isCCSelected && isExtended && selectedCaption && QRMenuClientId && company) {
        const url = `${process.env.REACT_APP_QRMENU_URL}/?table=${selectedCaption}&register=${QRMenuClientId}&guid=${company}`
        finalUrl = await getEncodedOrRaw(url, ['register', 'guid'])
      }

      setQrCodeValueInner(finalUrl)

      if (isExtended && finalUrl?.length) {
        const tableCaption = tablesList?.find(t => t.id === selectedCaption)?.caption || ''
        changeQrCodeValue(finalUrl, tableCaption)
      }
    } catch (e) {
      console.error('Error in *handleBranchChange*:', e)
    } finally {
      setIsLinkGenerating(false)
    }
  }

  const copyUrlToClipBoard = () => {
    setIsToasterOpen(true)
    navigator.clipboard.writeText(qrCodeValueInner)
  }

  const handleDownload = async () => {
    const resultedCode = document.querySelector('#resultedCode')
    const company = await getGuid()
    if (resultedCode) {
      html2canvas(resultedCode as HTMLElement).then(canvas => {
        canvas.toBlob(blob => {
          const pngName = isCCSelected
            ? `cc-${company}.png`
            : `${selectedBranch}-${
                branchList?.find(item => +item.Intern === Number(selectedBranch))?.Bezeichnung
              }.png`
          if (blob) {
            saveAs(blob, pngName)
          }
        })
      })
    }
  }

  return (
    <QRCodeLayout>
      <MessageCopiiedToaster isOpen={isToasterOpen} onClose={() => setIsToasterOpen(false)} />
      <GridDiv $gc='1 / 2' $gr='1 / -1'>
        <QRCodeContainer id='resultedCode' isExtended={isExtended}>
          <RenderIf condition={!!qrCodeValueInner?.length && !isLinkGenerating}>
            <QRCodeCanvas value={qrCodeValueInner} size={250} />
            <RenderIf condition={isExtended}>
              <QRCodeText>
                Table {tablesList?.find(el => el.id === selectedCaption)?.caption}
              </QRCodeText>
            </RenderIf>
          </RenderIf>
          <RenderIf condition={!qrCodeValueInner?.length && !isLinkGenerating}>
            <DisclaimerText>
              Select all needed values and generate QR-Code and the link by pressing "Generate link"
              button.
            </DisclaimerText>
          </RenderIf>
          <RenderIf condition={isLinkGenerating}>
            <DisclaimerText>Generating QR Code...</DisclaimerText>
          </RenderIf>
        </QRCodeContainer>
      </GridDiv>
      <GridDiv $gc='2 / -1' $gr='1 / 2'>
        <SelectContainer>
          <RenderIf condition={isExtended}>
            <GridDiv $gc='1 / 2' $gr='1 / 2'>
              <Select
                key={restaurantDesignId}
                label='Room'
                value={Number(selectedRoom)}
                onChange={value => setSelectedRoom(+value)}
                options={roomsList
                  ?.filter(room1 => {
                    if (restaurantDesignId) {
                      return room1.restaurantDesignId === restaurantDesignId
                    }
                    return true
                  })
                  ?.map(room2 => ({ label: room2.caption, value: room2.id }))}
              />
            </GridDiv>
            <GridDiv $gc='2 / -1' $gr='1 / 2'>
              <Select
                label='Caption'
                value={Number(selectedCaption)}
                onChange={value => setSelectedCaption(+value)}
                options={tablesList
                  ?.filter(item1 => item1.roomId === selectedRoom)
                  ?.map(item2 => ({ label: item2.caption, value: item2.id }))
                  ?.sort((a, b) =>
                    a.label.localeCompare(b.label, undefined, {
                      numeric: true,
                      sensitivity: 'base'
                    })
                  )}
              />
            </GridDiv>
            <GridDiv $gc='1 / -1' $gr='2 / -1'>
              <Button
                isLoading={isLinkGenerating}
                handleClick={generateLink}
                isDisabled={!!qrCodeValueInner?.length}
                height='56px'
                width='100%'
              >
                Generate link
              </Button>
            </GridDiv>
          </RenderIf>
        </SelectContainer>
      </GridDiv>
      <GridDiv $gc='2 / -1' $gr='2 / -1'>
        <SelectContainerBottomAligned>
          <GridDiv $gc='1 / -1' $gr='1 / 2'>
            <Input value={qrCodeValueInner} disabled fullWidth />
          </GridDiv>
          <GridDiv $gc='1 / 2' $gr='2 / -1'>
            <Button
              isDisabled={!qrCodeValueInner?.length}
              handleClick={copyUrlToClipBoard}
              variant='outlined'
              height='56px'
              width='100%'
            >
              Copy link
            </Button>
          </GridDiv>
          <GridDiv $gc='2 / -1' $gr='2 / -1'>
            <Button
              isDisabled={!qrCodeValueInner?.length}
              handleClick={handleDownload}
              height='56px'
              width='100%'
            >
              Download
            </Button>
          </GridDiv>
        </SelectContainerBottomAligned>
      </GridDiv>
    </QRCodeLayout>
  )
}

type QRCodeBuilderType = {
  isExtended?: boolean
}
